import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, type ReactNode } from 'react';
import type { Principal } from '@saas-manager/shared';
import { api, ApiError } from './api';

export interface Me {
  user: { id: string; email: string; name: string; role: 'owner' | 'admin' | 'member'; mfaEnabled: boolean; lastLoginAt: string | null; hasPassword: boolean };
  mfa: 'verified' | 'required' | 'setup_required' | 'not_required';
  principal: Principal;
  session: { id: string; expiresAt: string };
}

interface SessionValue {
  me: Me | null;
  principal: Principal | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const Ctx = createContext<SessionValue>({ me: null, principal: null, loading: true, refresh: async () => undefined });

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery<Me | null>({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        return await api.get<Me>('/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
  const value: SessionValue = {
    me: q.data ?? null,
    principal: q.data?.principal ?? null,
    loading: q.isLoading,
    refresh: async () => {
      await qc.invalidateQueries({ queryKey: ['session'] });
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useSession = () => useContext(Ctx);
